/**
 * Friendly UX text for the raw ``lanes.status`` enum.
 *
 * Never expose the raw enum to the user — "starting" looks broken when
 * it's actually fine; "stopped" sounds permanent when it's not.
 */
export type RawLaneStatus =
  | 'starting' | 'idle' | 'busy' | 'crashed' | 'stopped' | string | null

export type FriendlyStatus = {
  label: string
  tone: 'green' | 'amber' | 'red' | 'blue' | 'stone'
  blink: boolean
  description: string
}

export type ProgressContext = {
  lastProgressAt?: number | null
  lastProgressKind?: string | null
  busySince?: number | null
  startedAt?: number | null
}

const PROGRESS_STALL_S = 20 * 60

function ageSeconds(since?: number | null): number | null {
  return since ? Math.max(0, Math.floor(Date.now() / 1000) - since) : null
}

function shortAge(ageS: number): string {
  if (ageS < 45) return 'now'
  if (ageS < 3600) return `${Math.floor(ageS / 60)}m`
  if (ageS < 86_400) return `${Math.floor(ageS / 3600)}h`
  return `${Math.floor(ageS / 86_400)}d`
}

function progressKindLabel(kind?: string | null): string {
  if (kind === 'tool_call_progress') return 'tool checkpoint'
  if (kind === 'agent_reply_delta') return 'reply checkpoint'
  return 'checkpoint'
}

function runLabel(ageS: number): string {
  // A turn that just started has no useful duration yet. "now" is reserved
  // for a checkpoint that happened this moment, and must not be the label
  // of the run itself.
  if (ageS < 45) return 'Running'
  return `Running ${shortAge(ageS)}`
}

function runPhrase(ageS: number): string {
  if (ageS < 45) return 'a few seconds'
  return shortAge(ageS)
}

function checkpointPhrase(ageS: number): string {
  if (ageS < 45) return 'just now'
  return `${shortAge(ageS)} ago`
}

export function activeWorkStatusFriendly(
  raw: 'working' | 'queued' | null | undefined,
  progress: ProgressContext = {},
): FriendlyStatus {
  if (raw === 'queued') {
    return { label: 'Queued', tone: 'amber', blink: false,
             description: 'Waiting its turn behind earlier work.' }
  }
  if (raw !== 'working') {
    return { label: '', tone: 'green', blink: false, description: 'Idle.' }
  }

  // The label is how long this message has been the active turn. A fresh
  // tool or reply checkpoint only means the turn is still alive — it must
  // not reset a hours-long run to "Checkpoint now".
  const runAge = ageSeconds(progress.startedAt ?? progress.busySince)
  const progressAge = ageSeconds(progress.lastProgressAt)
  const kind = progressKindLabel(progress.lastProgressKind)
  const label = runAge === null ? 'Running' : runLabel(runAge)
  const ranFor = runAge === null ? 'this turn' : runPhrase(runAge)

  if (progressAge !== null && progressAge > PROGRESS_STALL_S) {
    return {
      label,
      tone: 'amber',
      blink: true,
      description: `Still running for ${ranFor}, but the last ${kind} was ${checkpointPhrase(progressAge)}.`,
    }
  }
  if (progressAge !== null) {
    return {
      label,
      tone: 'blue',
      blink: true,
      description: `Running for ${ranFor}. Last ${kind} was ${checkpointPhrase(progressAge)}.`,
    }
  }
  if (runAge !== null && runAge > PROGRESS_STALL_S) {
    return {
      label,
      tone: 'amber',
      blink: true,
      description: `Still running for ${ranFor}, but this turn has no progress checkpoint.`,
    }
  }
  return {
    label,
    tone: 'blue',
    blink: true,
    description: runAge === null
      ? 'Agent is processing a turn; no checkpoint yet.'
      : `Running for ${ranFor}. No checkpoint yet.`,
  }
}

export function laneStatusFriendly(
  raw: RawLaneStatus,
  lastHeartbeat?: number | null,
  progress: ProgressContext = {},
  liveStream: boolean = true,
): FriendlyStatus {
  const ageS = ageSeconds(lastHeartbeat)
  // Heartbeat freshness is only meaningful when we have a live data
  // stream (WS open). On a cold app start the IDB-cached heartbeat is
  // hours old even when the backend is healthy — pessimistically marking
  // every row "Reconnecting" until the first /api/chats response lands
  // was a false positive that blinked the whole list on every launch.
  const stale = liveStream && ageS !== null && ageS > 180

  // Liveness loss wins over raw status. A killed lane can still have
  // raw status='busy' until reconciliation catches up.
  if (stale) {
    return { label: 'Reconnecting', tone: 'amber', blink: true,
             description: `No lane liveness signal for ${shortAge(ageS!)}.` }
  }

  if (raw === 'idle') {
    // No text for the healthy/idle state — Mark wants the green dot
    // alone (saves header + chat-list-row space). Other states keep
    // their labels because the dot colour alone doesn't disambiguate
    // (e.g. amber could mean Connecting / Paused / Reconnecting).
    return { label: '', tone: 'green', blink: false,
             description: 'Agent is idle and waiting for you.' }
  }
  if (raw === 'busy') {
    return activeWorkStatusFriendly('working', progress)
  }
  if (raw === 'starting') {
    return { label: 'Connecting…', tone: 'amber', blink: true,
             description: 'Spawning the agent process. Usually takes 10–30s.' }
  }
  if (raw === 'crashed') {
    return { label: 'Offline', tone: 'red', blink: false,
             description: 'Agent crashed. The watchdog will restart it on the next pass.' }
  }
  if (raw === 'stopped') {
    return { label: 'Paused', tone: 'amber', blink: false,
             description: 'Agent was stopped cleanly. Restart the controller to bring it back.' }
  }
  return { label: raw || 'Unknown', tone: 'stone', blink: false, description: '' }
}

export const TONE_BG: Record<FriendlyStatus['tone'], string> = {
  green: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red:   'bg-red-500',
  blue:  'bg-blue-500',
  stone: 'bg-stone-400',
}

/** Per-topic agent status (independent of the lane's status).
 *
 *  Mark's rule: when viewing a topic, the badge should reflect ONLY that
 *  topic — not "any other sibling topic in the same group". So the chat
 *  header dot when in topic mode reads from this, not from
 *  ``laneStatusFriendly``.
 *
 *  Inputs come from the topics endpoint as a derived enum:
 *    - 'working' → at least one message in this topic is dispatched
 *    - 'queued'  → at least one message is queued (waiting its turn)
 *    - 'error'   → most recent message is an error
 *    - 'idle'    → otherwise
 */
export function topicStatusFriendly(
  raw: 'working' | 'queued' | 'error' | 'idle' | null | undefined,
  progress: ProgressContext = {},
): FriendlyStatus {
  if (raw === 'working') {
    return activeWorkStatusFriendly('working', progress)
  }
  if (raw === 'queued') {
    return activeWorkStatusFriendly('queued', progress)
  }
  if (raw === 'error') {
    return { label: 'Errored', tone: 'red', blink: false,
             description: 'The last turn for this topic failed.' }
  }
  // Default: idle (or unknown) — green dot, no label (Mark's "Ready text removed" rule).
  return { label: '', tone: 'green', blink: false,
           description: 'Topic is idle.' }
}
